import React from 'react';
import { Combobox } from 'components/ui/combobox';
import { Download, X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  month: number;
  onMonthChange: (m: number) => void;
  months: { value: number; label: string }[];
  year: string;
  monthRange: string;
  filters: Array<{ label: string; value: string }>;
  onDownload: () => void;
}

const BulkDownloadModal: React.FC<Props> = ({
  open,
  onClose,
  month,
  onMonthChange,
  months,
  year,
  monthRange,
  filters,
  onDownload,
}) => {
  if (!open) return null;

  const isMonthSelected = month > 0 && !isNaN(month);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md shadow-xl p-6 space-y-5 animate-in fade-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Bulk Excel Download</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Select last month</label>
          <Combobox
            options={months.map((m) => ({
              value: String(m.value),
              label: m.label,
            }))}
            value={String(month)}
            onValueChange={(val) => onMonthChange(Number(val))}
          />
        </div>

        <div className="rounded-md border bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-2">
          <span className="font-medium">Summary</span>
          <div className="flex">
            <span className="w-28 shrink-0 font-medium text-gray-900">Year:</span>
            <span className="break-words">{year}</span>
          </div>

          <div className="flex">
            <span className="w-28 shrink-0 font-medium text-gray-900">Months:</span>
            <span className="break-words">{monthRange}</span>
          </div>

          {filters.map((f) => (
            <div key={f.label} className="flex">
              <span className="w-28 shrink-0 font-medium text-gray-900">{f.label}:</span>
              <span className="min-w-0 break-words">{f.value}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <button
            className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className={`px-4 py-2 text-sm rounded-md flex items-center gap-2 transition-colors ${isMonthSelected
              ? 'bg-gray-900 text-white hover:bg-black cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            disabled={!isMonthSelected}
            onClick={() => {
              if (isMonthSelected) {
                onDownload();
                onClose();
              }
            }}
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDownloadModal;
